import React, { useCallback, useMemo } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { Default, Rightrow1, Statusrow, Status, Icon2, DropDiv } from './styles';
import { Autotext, Box, Leftrow, Row, Subtitle, Title } from '../style';
import ButtonV2 from '../../button/buttonV2';
import { Dropdown, DropdownItem } from '../../Dropdown';
import { PrioritiesList } from '../../../global/row';
import { updateProjectData } from '../../../services/projectServices';
import { rootStore } from '../../../mobx/rootStore';
import { useDispatch } from 'react-redux';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE } from '../../../global/constants';

interface Props {
  themeMode: { theme: string };
  onUpdatePriority: (value: boolean) => void;
  loadingButton: boolean;
  isTemplate?: boolean;
}

const PrioritySettingCard: React.FC<Props> = (props) => {
  const { onUpdatePriority, loadingButton, isTemplate = false } = props;
  //Other variables
  const dispatch = useDispatch();

  //Mobx store variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { currentProject: projectDetails } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const currentProject = isTemplate ? currentProjectTemplate : projectDetails;

  // for set default priority
  const onChangeDefault = useCallback(
    async (key: number) => {
      if (isTemplate) {
        rootStore.projectTemplateStore.currentProjectTemplate?.setDefaultPriority(key);
      } else {
        const payload = {
          Priority: {
            Enabled: currentProject?.priority?.enabled,
            Default: key
          }
        };
        rootStore.projectStore.currentProject?.setDefaultPriority(key);
        await dispatch(updateProjectData(currentProject?.id, payload));
      }
    },
    [dispatch, currentProject, isTemplate]
  );

  // Memoize the rendered list of priorities
  const priorityElements = useMemo(() => {
    return PrioritiesList.map((priority) => (
      <Statusrow key={priority.key}>
        <Rightrow1>
          <SVGIcon name={priority.icon} width='16' height='16' viewBox='0 0 16 16' />
          <Title>{priority.title}</Title>
        </Rightrow1>
        <Rightrow1>
          {currentProject?.priority?.default === priority.key ? (
            <Default>Default</Default>
          ) : (
            <DropDiv>
              <Dropdown
                modalTitle='Priority'
                activeClassName='active-state'
                content={
                  <Icon2>
                    <SVGIcon
                      name='three-dots-horizontal-icon'
                      width='16'
                      height='16'
                      viewBox='0 0 18 18'
                      fill='var(--text-secondary)'
                    />
                  </Icon2>
                }
                trigger='click'>
                <DropdownItem
                  onClick={() => {
                    if (priority.key !== undefined) {
                      onChangeDefault(priority.key);
                    }
                  }}
                  label='Set as Default'
                />
              </Dropdown>
            </DropDiv>
          )}
        </Rightrow1>
      </Statusrow>
    ));
  }, [currentProject?.priority?.default, onChangeDefault]);

  return (
    <Box>
      <Row>
        <Leftrow>
          <Title>Priorities</Title>
          <Subtitle>
            Priorities communicate and prioritize tasks. Issues are linearly ordered by default, but can be changed in
            different views.
          </Subtitle>
        </Leftrow>
        <Autotext>
          {loadingButton ? (
            <ButtonV2 variant={BUTTON_TYPE.PRIMARY} isLoading={loadingButton} />
          ) : (
            <label className='switch'>
              <input
                type='checkbox'
                checked={currentProject?.priority?.enabled || false}
                onChange={() => onUpdatePriority(!currentProject?.priority?.enabled)}
              />
              <span className='slider round'></span>
            </label>
          )}
        </Autotext>
      </Row>
      {currentProject?.priority?.enabled === true && <Status>{priorityElements}</Status>}
    </Box>
  );
};

export default observer(PrioritySettingCard);
